import React, { useState, useEffect } from 'react';
import { Router } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import { createBrowserHistory } from 'history';
import MomentUtils from '@date-io/moment';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider } from '@material-ui/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { getEnvVars } from 'src/config/env.config';
import { snackbarUIConfig } from 'src/config';
import { setEnvVars, getDataFromSessionStorage } from 'src/utils/general.utils';
import {
  setEnvVarsForHttpClient,
  setEnvEndpoints,
  createAxiosInstance,
} from 'src/api/http.service';
import { apiEndpoints } from 'src/config/api.config';
import { InitMockRunner } from 'src/mock/trigger.mock';

import { createTranscoderAxiosInstance } from 'src/api/transcodingHttp.service';
import './mixins/chartjs';
import './mixins/moment';
import './mixins/validate';
import './mixins/prismjs';
import './mock';
import './assets/scss/main.scss';
import { SnackbarUIComponent, CircularIndeterminate } from 'src/reuseableComponents';

import { LoginSuccessAction, LogoutAction } from 'src/store/actions/loginManagement.actions';

import { SnackbarProvider } from 'notistack';
import Chatbar from './views/FileExplorer/chatbar';
import DirectionToggle from './components/DirectionToggle';
import StylesProvider from './components/StylesProvider';
import ScrollReset from './components/ScrollReset';
import GoogleAnalytics from './components/GoogleAnalytics';
import routes from './routes';
import { theme, themeWithRtl } from './theme';
import { LicenseInfo } from '@mui/x-data-grid-pro';

LicenseInfo.setLicenseKey(
  '7c6efbc7d064d8c1e907915f10de22a1Tz02MTg4MixFPTE3MTAzMTExOTgwODYsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
);

// Initializing critical methods for http, mock and env setup of the application
setEnvVars(getEnvVars());
setEnvVarsForHttpClient(getEnvVars());
setEnvEndpoints(apiEndpoints);
createAxiosInstance();
createTranscoderAxiosInstance();
InitMockRunner();

// create a demo profile with session storage for non-prod env to demonstrate
// all envs by just changing session storage values without any code change.
// *****************************************************************************
// eslint-disable-next-line no-undef
const history = createBrowserHistory({ basename: process.env.PUBLIC_URL });

function App() {
  const [direction, setDirection] = useState('ltr');
  const dispatch = useDispatch();

  const uiState = useSelector(state => state.uiState);
  const {
    error: { isAuthTokenValid },
  } = uiState;
  const { loginSuccess } = useSelector(state => state.loginState);

  useEffect(() => {
    createAxiosInstance();
    createTranscoderAxiosInstance();
  }, [loginSuccess]);

  useEffect(() => {
    const token = getDataFromSessionStorage('authToken', false);
    if (token && token !== null) {
      dispatch(LoginSuccessAction());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isAuthTokenValid === false) {
      dispatch(LogoutAction()).then(data => {
        if (data) {
          window.location = '/auth/login';
        }
      });
    }
    // eslint-disable-next-line
  }, [isAuthTokenValid]);

  const handleDirecitonToggle = () => {
    setDirection(prevDirection => (prevDirection === 'ltr' ? 'rtl' : 'ltr'));
  };
  return (
    <div data-test="app-component">
      <ThemeProvider theme={direction === 'rtl' ? themeWithRtl : theme}>
        <SnackbarProvider
          maxSnack={snackbarUIConfig.maxSnack}
          autoHideDuration={snackbarUIConfig.autoHideDuration}
        >
          <StylesProvider direction={direction}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Router history={history}>
                <ScrollReset />
                <GoogleAnalytics />
                <DirectionToggle direction={direction} onToggle={handleDirecitonToggle} />
                {renderRoutes(routes)}
                <Chatbar />
              </Router>
              <SnackbarUIComponent uistate={uiState} />
              <CircularIndeterminate showSpinner={uiState.showSpinner} />
            </MuiPickersUtilsProvider>
          </StylesProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
